import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Feather } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL hash fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          navigate('/login');
          return;
        }

        // Exchange session_id for session_token via backend
        const { data } = await axios.post(
          `${API}/auth/google/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true,
          }
        );

        // Update auth context and navigate to dashboard
        setUser(data);
        window.history.replaceState(null, '', '/dashboard');
        navigate('/dashboard', { replace: true, state: { user: data } });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login?error=auth_failed');
      }
    };

    processSession();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-4" data-testid="auth-callback">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="bg-amber-100 p-4 rounded-full">
              <Feather className="h-10 w-10 text-amber-800" />
            </div>
            <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-500 border-2 border-white animate-pulse" />
          </div>
        </div>

        <h1 className="font-display text-3xl font-bold text-amber-950 mb-2">Welcome back</h1>
        <p className="text-amber-800/70 mb-8">Turning a fresh page for you&hellip;</p>

        <div className="flex justify-center space-x-2 mb-6" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-700 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="h-2.5 w-2.5 rounded-full bg-orange-600 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>

        <p className="font-serif-italic text-amber-800/60 text-sm">
          &ldquo;The day rolled out its Autumn carpet&hellip;&rdquo;
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
